import * as z from "zod";
import { userFacingError } from "../authorization";
import { isUserFacingError } from "../userFacingError";
import { zql } from "../generatedDrizzleSchema";
import { mutator } from "./mutator";
import { interruptSubAgentChild } from "./stopSubAgent";
import { insertUserInterruptEntry } from "./utils";
import { clientGeneratableIdSchema } from "../id";

export const interruptPersonalChatSchema = z.object({
  entryId: clientGeneratableIdSchema,
  chatId: z.string(),
});

export const awaitedSubAgents = (chatId: string) =>
  zql.subAgents
    .where("parentChatId", chatId)
    .where("awaitingEntryId", "IS NOT", null);

export const interruptPersonalChat = mutator({
  schema: interruptPersonalChatSchema,
  resolve: {},
  permissions: {
    none: "the chat is resolved through the caller’s own personalChats row; entries are written only into that chat",
  },
  fn: async ({ tx, ctx, args: { entryId, chatId } }) => {
    const { userId } = ctx;
    const [pc] = await tx.run(
      zql.personalChats.where("chatId", chatId).where("userId", userId)
    );
    if (!pc) throw userFacingError("Personal chat not found");

    const chat = await tx.run(zql.chats.where("id", chatId).one());
    const createdAt = Date.now();

    const lastEntries = await tx.run(
      zql.chatEntries
        .where("chatId", chatId)
        .orderBy("createdAt", "desc")
        .limit(1)
    );
    const previousEntryId = lastEntries[0]?.id ?? null;

    await insertUserInterruptEntry(tx, {
      id: entryId,
      chatId,
      createdAt,
      previousEntryId,
      tenantId: ctx.tenantId,
    });

    await tx.mutate.chats.update({
      id: chatId,
      lastChatEntryType: "UserInterrupt",
      awaitingAction: false,
      respondingAt: null,
    });

    await tx.mutate.personalChats.update({
      id: pc.id,
      lastActiveAt: createdAt,
    });

    // The user stopped this turn, so children it awaits or that asked for input during it stop without a result that would wake it again.
    const turnStart = chat?.respondingAt ?? null;
    const children = await tx.run(
      zql.subAgents
        .where("parentChatId", chatId)
        .where(({ or, cmp }) =>
          or(
            cmp("awaitingEntryId", "IS NOT", null),
            cmp("status", "needs_input")
          )
        )
    );
    for (const agent of children.filter(
      (child) =>
        child.awaitingEntryId !== null ||
        (turnStart !== null && child.updatedAt >= turnStart)
    )) {
      // A child the user can no longer reach mustn’t stop them stopping their own chat.
      if (agent.status === "running" || agent.status === "needs_input")
        await interruptSubAgentChild(
          tx,
          ctx,
          agent,
          `${entryId}-${agent.id}`
        ).catch((error) => {
          if (!isUserFacingError(error)) throw error;
        });
      await tx.mutate.subAgents.update({
        id: agent.id,
        awaitingEntryId: null,
        deliveryPendingAt: null,
      });
    }
  },
});
